import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { residentHealthRecordsConstants } from 'locale/residentHealthRecords'
import {
  EmptyState,
  NoDataIcon,
  Spinner,
  ListItem,
  NavigationModal
} from '@lmc/ui-kit'
import { useCommunicationRecordsList } from '../hooks/useCommunicationRecordsList'
import { CommunicationRecord, UserRole } from '@lmc/ts-common'
import styled from 'styled-components'
import { EllipsisOutlined, FileTextOutlined } from '@ant-design/icons'
import { Text } from '@lmc/ui-kit/src/general'
import { formatDate, getStatusLabel } from '../utils/communicationRecordUtils'
import { Dropdown, MenuProps } from 'antd'
import { useState } from 'react'
import { useRoleAccess } from 'components/AccessControl'

type MenuInfo = Parameters<NonNullable<MenuProps['onClick']>>[0]

type CommunicationRecordListProps = {
  residentId: string
  onViewRecord?: (record: CommunicationRecord) => void
  onEditRecord?: (record: CommunicationRecord) => void
  onDeleteRecord?: (record: CommunicationRecord) => void
}

const CommunicationRecordList = ({
  residentId,
  onViewRecord,
  onEditRecord,
  onDeleteRecord
}: CommunicationRecordListProps) => {
  const { t } = useTranslation(NAMESPACE.RESIDENT_HEALTH_RECORDS)
  const { communicationRecords, isLoading } = useCommunicationRecordsList({
    residentId,
    enabled: !!residentId
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [recordToDelete, setRecordToDelete] =
    useState<CommunicationRecord | null>(null)

  const canDeleteCommunicationRecords = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'delete',
        this: 'CommunicationRecord'
      }
    ]
  )

  const canEditCommunicationRecords = useRoleAccess(
    [UserRole.lmcAdmin, UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'CommunicationRecord'
      }
    ]
  )

  if (isLoading) {
    return <Spinner style={{ minHeight: 200 }} />
  }

  if (!communicationRecords || communicationRecords.length === 0) {
    return (
      <EmptyState
        icon={<NoDataIcon />}
        title={t(residentHealthRecordsConstants.NO_COMMUNICATION_RECORDS)}
        description={t(
          residentHealthRecordsConstants.NO_COMMUNICATION_RECORDS_DESCRIPTION
        )}
      />
    )
  }

  const getActionItems = (record: CommunicationRecord): MenuProps['items'] => {
    return [
      ...(record.document?.url
        ? [
            {
              key: 'view',
              label: t(residentHealthRecordsConstants.VIEW),
              onClick: (e: MenuInfo) => {
                e.domEvent?.stopPropagation()
                onViewRecord?.(record)
              }
            }
          ]
        : []),
      ...(canEditCommunicationRecords
        ? [
            {
              key: 'edit',
              label: t(residentHealthRecordsConstants.EDIT),
              onClick: (e: MenuInfo) => {
                e.domEvent?.stopPropagation()
                onEditRecord?.(record)
              }
            }
          ]
        : []),
      ...(canDeleteCommunicationRecords
        ? [
            {
              key: 'delete',
              label: t(residentHealthRecordsConstants.DELETE),
              onClick: (e: MenuInfo) => {
                e.domEvent?.stopPropagation()
                setRecordToDelete(record)
                setShowDeleteModal(true)
              },
              danger: true
            }
          ]
        : [])
    ]
  }

  const handleConfirmDelete = () => {
    if (recordToDelete) {
      onDeleteRecord?.(recordToDelete)
      setShowDeleteModal(false)
      setRecordToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setRecordToDelete(null)
  }

  return (
    <>
      <ListContainer>
        {communicationRecords.map((record: CommunicationRecord) => (
          <ListItem
            key={record.id}
            iconContent={<StyledFileTextOutlined />}
            titleContent={<Text weight="bold">{record.title}</Text>}
            subTitleContent={
              <CommunicationInfoContainer>
                <SubTitleContainer>
                  {record.dateSent && (
                    <>
                      <LightText level={1.5}>
                        {formatDate(
                          record.dateSent instanceof Date
                            ? record.dateSent.toISOString()
                            : record.dateSent
                        )}
                      </LightText>
                      <LightText level={1.5}>•</LightText>
                    </>
                  )}
                  <LightText level={1.5}>
                    {t(getStatusLabel(record.communicationStatus))}
                  </LightText>
                </SubTitleContainer>
                <LightText level={1.5}>{record.description}</LightText>
              </CommunicationInfoContainer>
            }
            rightChildren={
              <RightContent>
                <Dropdown
                  menu={{ items: getActionItems(record) }}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <StyledEllipsisOutlined />
                  </div>
                </Dropdown>
              </RightContent>
            }
            hasBorder
            onClick={() => onViewRecord?.(record)}
            cursor="pointer"
            alignChildrenTop
          />
        ))}
      </ListContainer>
      <NavigationModal
        showPrompt={showDeleteModal}
        modalTitle={t(
          residentHealthRecordsConstants.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_COMMUNICATION_RECORD
        )}
        modalContentText={t(
          residentHealthRecordsConstants.ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_COMMUNICATION_RECORD_DESCRIPTION
        )}
        modalConfirmText={t(residentHealthRecordsConstants.DELETE)}
        modalCancelText={t(residentHealthRecordsConstants.CANCEL)}
        confirmNavigation={handleConfirmDelete}
        cancelNavigation={handleCancelDelete}
      />
    </>
  )
}

export default CommunicationRecordList

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 1080px;
  margin: 0 auto;
`

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`
const LightText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const SubTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`

const StyledFileTextOutlined = styled(FileTextOutlined)`
  font-size: 20px;
`

const StyledEllipsisOutlined = styled(EllipsisOutlined)`
  font-size: 24px;
`

const CommunicationInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: justify;
`
